import { DatePickerTriggerButton } from "$root/components/date-picker-popover";
import { useLocaleFormatters } from "$root/localization/hooks";
import { type TextProps, Text } from "@mdotm/shadcn/legacy/components";
import { useTranslation } from "react-i18next";
import { AsOfDatePicker } from "./AsOfDatePicker";
import type { AsOfSnapshotState } from "./useAsOfSnapshot";

/**
 * The "As of" label with the date button that opens the as-of picker (MDOT-24733, MDOT-24860). Every
 * surface that can pin a date uses this one control, so entering a pinned view and moving within it
 * are one gesture: without a pinned date it reads "Today", and with one it reads the pinned date.
 */
export function AsOfButton(props: {
	/** Validated as-of date (`YYYY-MM-DD`) the page is pinned to; unset = live view, reads "Today". */
	asOf?: string;
	setAsOf(asOf: string | null): void;
	/** Whether the pinned date's snapshot can be read yet; absent in the live view. */
	snapshot?: AsOfSnapshotState;
	/** Portfolio inception date (`YYYY-MM-DD` or full ISO timestamp) — earliest selectable day. */
	inceptionDate?: string;
	/** Per-surface e2e hook — each surface needs its own address. */
	qualifier: string;
	/**
	 * Typography for the "As of" label, for a surface whose neighbouring labels have their own — the
	 * report chooser's table, where it sits among column headers. Omitted, the label inherits the text
	 * around it, which is what the header subtitle and the white-on-dark `AsOfBar` need.
	 */
	label?: { type: TextProps["type"]; color?: TextProps["color"] };
	/** Greys the trigger and stops it opening, for a surface with nothing to pin a date to yet. */
	disabled?: boolean;
}): React.JSX.Element {
	const { t } = useTranslation();
	const { formatDate } = useLocaleFormatters();

	// Read in UTC, as the report's own subtitle and the downloaded filename are: a pinned date is a
	// calendar day, and rendering it in the browser's zone shows the day before west of UTC.
	const label = props.asOf ? formatDate(props.asOf, { timezone: "UTC" }) : t("PORTFOLIO_DETAILS.AS_OF_PICKER.TODAY");
	// "Today" says where the page is, not what the button does — so the live view's accessible name
	// says the latter. Once a date is pinned the date itself is the thing worth announcing.
	const ariaLabel = props.asOf
		? `${t("PORTFOLIO_DETAILS.AS_OF_PICKER.LABEL")}: ${label}`
		: t("PORTFOLIO_DETAILS.AS_OF_PICKER.VIEW_AS_OF_DATE");

	const labelClassName = "mr-2 uppercase whitespace-nowrap";
	const asOfWord = t("PORTFOLIO_DETAILS.AS_OF_PICKER.LABEL");

	return (
		<div className="flex items-center">
			{props.label ? (
				<Text as="span" type={props.label.type} color={props.label.color} className={labelClassName}>
					{asOfWord}
				</Text>
			) : (
				<span className={labelClassName}>{asOfWord}</span>
			)}
			<AsOfDatePicker
				asOf={props.asOf}
				setAsOf={props.setAsOf}
				snapshot={props.snapshot}
				inceptionDate={props.inceptionDate}
			>
				<DatePickerTriggerButton
					label={label}
					ariaLabel={ariaLabel}
					disabled={props.disabled}
					qualifier={props.qualifier}
				/>
			</AsOfDatePicker>
		</div>
	);
}
